import { S3Client, PutObjectCommand, DeleteObjectCommand, ListObjectsCommand } from '@aws-sdk/client-s3';
import { Buffer } from 'buffer';
import { extractBase64String, formatUniquePublicID, getIsImageFile, getIsPDFFile } from '../utils/utilities';
import { PDFImageProps } from '../utils/models';
import { transformPDFFileToImage } from './cloudinaryService';
import { MAX_FILE_SIZE_MB } from '../utils/constants';
import { updateLastPhotoNumber } from '../store/reportDetails';

import _ from 'lodash';
import imageCompression from 'browser-image-compression';

import config from '../utils/config';
import toastService from './toastService';
import httpService from './httpService';

const BUCKET_NAME = process.env.REACT_APP_BUCKET_NAME as string;
const REGION = process.env.REACT_APP_REGION as string;
const ACCESS_KEY = process.env.REACT_APP_ACCESS_KEY as string;
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY as string;

const s3 = new S3Client({
    region: REGION,
    credentials: {
        accessKeyId: ACCESS_KEY,
        secretAccessKey: SECRET_KEY,
    }
});

const getS3BaseURL = () => {
    return `https://rsg-aidms.s3.eu-north-1.amazonaws.com`;
};

const getS3FileURL = (fileName: string) => {
    return `${process.env.REACT_APP_OFFICIAL_URL}/${fileName}`;
};

const getFileFormat = (file: File) => {
    return file.type.split('/')[1];
}

const uploadFileUrlToS3 = async (image: PDFImageProps) => {
    const formattedFileName = formatUniquePublicID(image.caption);

    try {
        const response = await httpService.get(image.src, { responseType: 'arraybuffer' });
    
        const command = new PutObjectCommand({
            ACL: 'public-read',
            Bucket: BUCKET_NAME,
            Key: formattedFileName,
            Body: Buffer.from(response.data, 'base64'),
            ContentEncoding: 'base64',
            ContentType: image.format,
            ContentDisposition: 'inline'
        });

        await s3.send(command);

        return {
            format: image.format,
            src: getS3FileURL(formattedFileName),
            refNo: image.refNo,
            caption: image.caption,
        }
    } catch (error) {
        console.log(error);
        toastService.error('Failed to upload: ' + formattedFileName);

        return null;
    }
};

const uploadBase64ToS3 = async (fileName: string, base64String: string) => {
    const formattedFileName = formatUniquePublicID(fileName);

    const getFileTypeFromBase64 = (base64: string) => {
        const startIndex = 'data:'.length;
        const endIndex = base64.indexOf(';base64,');
        const type = base64.substring(startIndex, endIndex);

        return type;
    };

   const command = new PutObjectCommand({
        ACL: 'public-read',
        Bucket: BUCKET_NAME,
        Key: formattedFileName,
        Body: Buffer.from(extractBase64String(base64String), 'base64'),
        ContentEncoding: 'base64',
        ContentType: getFileTypeFromBase64(base64String),
        ContentDisposition: 'inline'
    });

    try {
        await s3.send(command);
        return getS3FileURL(formattedFileName);
    } catch (error) {
		console.log('cloudinary upload error:', error);

        toastService.error('Failed to upload: ' + formattedFileName);

        return null;
    }
};

const uploadSingleFileToS3Server = async (file: File): Promise<PDFImageProps | null> => {
    const formattedFileName = formatUniquePublicID(file.name);
    
    const command = new PutObjectCommand({
        ACL: 'public-read',
        Bucket: BUCKET_NAME,
        Key: formattedFileName,
        Body: file,
        ContentType: file.type,
        ContentDisposition: 'inline'
    });
    
    try {
        await s3.send(command);

        return {
            refNo: updateLastPhotoNumber(),
            src: getS3FileURL(formattedFileName),
            caption: formattedFileName,
            format: getFileFormat(file),
        };
    } catch (error) {
        console.log(error);
        toastService.error('Failed to upload:' + file.name);

        return null;
    }
}

const uploadFileToS3 = async (files: File[]) => {
    let urls: PDFImageProps[] = [];

    for (const file of files) {
        if (getIsPDFFile(file) && config.enablePDFToPngConversion) {
            const pdfImages = await transformPDFFileToImage(file);

            const uploadPromise = pdfImages.map((pdfImage) => uploadFileUrlToS3(pdfImage))
            const result = await Promise.all(uploadPromise);

            const validResult = result.filter((resultItem) => resultItem) as PDFImageProps[];
            urls = urls.concat(validResult);
        } else {
            const compressedFile = (getIsImageFile(getFileFormat(file))) ? await imageCompression(file, { maxSizeMB: MAX_FILE_SIZE_MB }) : file;
            const result = await uploadSingleFileToS3Server(compressedFile);
            if (result) urls.push(result);
        }
    }

    return _.orderBy(urls, ['refNo'], ['asc']);
};

const deleteSingleFileFromServer = async (fileName: string) => {
    const command = new DeleteObjectCommand({
        Bucket: BUCKET_NAME,
        Key: fileName,
      });
    
      try {
            await s3.send(command);
      } catch (error) {            
        toastService.error('Failed to delete image!' + fileName);
      }
};

const getAllFiles = async () => {
    const command = new ListObjectsCommand({
        Bucket: BUCKET_NAME,
        MaxKeys: 10000
      });

    try {
        const result = await s3.send(command);
        return result.Contents;
    } catch (error) {
        toastService.error('Failed to list images!');
    }
};

export { deleteSingleFileFromServer, getAllFiles, getS3BaseURL, uploadFileUrlToS3, uploadBase64ToS3, uploadFileToS3, uploadSingleFileToS3Server };
